/**
 * Settings route — Theme selection and Claude permissions.
 */

import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Palette, Shield, Plus, X, Check, Save, Bot, Key, Trash2, Loader2, Eye, EyeOff, RefreshCw, LogOut, ExternalLink, Copy, KeyRound, Clock, AlertTriangle, Terminal } from "lucide-react";
import { useNavigate } from "@tanstack/react-router";
import { CorpoCard } from "../components/ui/CorpoCard";
import { CRTHeader } from "../components/ui/CRTHeader";
import { GlyphButton } from "../components/ui/GlyphButton";
import { StatusDot } from "../components/ui/StatusDot";
import { useTheme } from "../hooks/useTheme";
import { useClaudeAuthStatus, useClaudeLogin, useClaudeLogout, useClaudeAuthLog } from "../hooks/useClaudeAuth";
import { themes } from "../themes";
import type { AuthStatus, ClaudeAuthLogEntry } from "@black-wall/shared";

interface ClaudeConfig {
  permissionMode?: string;
  addDirs?: string[];
  allowedTools?: string[];
  defaultModel?: string;
  defaultEffort?: string;
}

interface ClaudeConfigResponse {
  config: ClaudeConfig;
  lastUpdated: number | null;
}

function ThemeCard({
  theme,
  isActive,
  onSelect,
}: {
  theme: (typeof themes)[0];
  isActive: boolean;
  onSelect: () => void;
}) {
  return (
    <div
      className={`
        relative p-4 border cursor-pointer transition-all
        ${isActive
          ? "border-[var(--bw-accent)] shadow-[0_0_12px_var(--bw-accent)]"
          : "border-[var(--bw-border)] hover:border-[var(--bw-accent)]"
        }
      `}
      style={{
        clipPath:
          "polygon(0 0, calc(100% - 10px) 0, 100% 10px, 100% 100%, 10px 100%, 0 calc(100% - 10px))",
        backgroundColor: theme.colors.bg,
      }}
      onClick={onSelect}
    >
      {/* Active indicator */}
      {isActive && (
        <div className="absolute top-2 right-2">
          <div
            className="w-3 h-3 rounded-full"
            style={{
              backgroundColor: theme.colors.accent,
              boxShadow: `0 0 8px ${theme.colors.accent}`,
            }}
          />
        </div>
      )}

      {/* Theme name */}
      <p
        className="font-bold text-sm uppercase tracking-wider mb-2"
        style={{ color: theme.colors.fg }}
      >
        {theme.name}
      </p>

      {/* Color swatches */}
      <div className="flex gap-2 mb-2">
        <div
          className="w-6 h-6 rounded-sm border border-white/20"
          style={{ backgroundColor: theme.colors.bg }}
          title="Background"
        />
        <div
          className="w-6 h-6 rounded-sm border border-white/20"
          style={{ backgroundColor: theme.colors.accent }}
          title="Accent"
        />
        <div
          className="w-6 h-6 rounded-sm border border-white/20"
          style={{ backgroundColor: theme.colors.fg }}
          title="Foreground"
        />
      </div>

      {/* Description */}
      <p
        className="text-xs"
        style={{ color: theme.colors.muted }}
      >
        {theme.description}
      </p>
    </div>
  );
}

export function SettingsRoute() {
  const queryClient = useQueryClient();
  const { themeId, setTheme } = useTheme();

  // Local state for Claude config editing
  const [permissionMode, setPermissionMode] = useState("acceptEdits");
  const [directories, setDirectories] = useState<string[]>([]);
  const [allowedTools, setAllowedTools] = useState<string[]>([]);
  const [newDir, setNewDir] = useState("");
  const [newTool, setNewTool] = useState("");
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [defaultModel, setDefaultModel] = useState("sonnet");
  const [defaultEffort, setDefaultEffort] = useState("medium");

  // Fetch current Claude config
  const { data: configData, isLoading } = useQuery({
    queryKey: ["claude-config"],
    queryFn: async () => {
      const res = await fetch("/api/settings/claude");
      return res.json() as Promise<ClaudeConfigResponse>;
    },
  });

  // Update local state when config loads
  useEffect(() => {
    if (configData?.config) {
      setPermissionMode(configData.config.permissionMode ?? "acceptEdits");
      setDirectories(configData.config.addDirs ?? []);
      setAllowedTools(configData.config.allowedTools ?? []);
      setDefaultModel(configData.config.defaultModel ?? "sonnet");
      setDefaultEffort(configData.config.defaultEffort ?? "medium");
    }
  }, [configData]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/settings/claude", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          permissionMode,
          addDirs: directories,
          allowedTools,
          defaultModel,
          defaultEffort,
        }),
      });
      return res.json();
    },
    onMutate: () => {
      setSaveStatus("saving");
    },
    onSuccess: () => {
      setSaveStatus("saved");
      queryClient.invalidateQueries({ queryKey: ["claude-config"] });
      setTimeout(() => setSaveStatus("idle"), 2000);
    },
    onError: () => {
      setSaveStatus("error");
    },
  });

  // Add directory mutation
  const addDirMutation = useMutation({
    mutationFn: async (dir: string) => {
      const res = await fetch("/api/settings/claude/add-dir", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ dir }),
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["claude-config"] });
    },
  });

  // Remove directory
  const removeDirMutation = useMutation({
    mutationFn: async (dir: string) => {
      const res = await fetch("/api/settings/claude/remove-dir", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ dir }),
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["claude-config"] });
    },
  });

  // Allow tool
  const allowToolMutation = useMutation({
    mutationFn: async (tool: string) => {
      const res = await fetch("/api/settings/claude/allow-tool", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tool }),
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["claude-config"] });
    },
  });

  // Deny tool
  const denyToolMutation = useMutation({
    mutationFn: async (tool: string) => {
      const res = await fetch("/api/settings/claude/deny-tool", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tool }),
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["claude-config"] });
    },
  });

  const handleAddDir = () => {
    const trimmed = newDir.trim();
    if (trimmed && !directories.includes(trimmed)) {
      setDirectories([...directories, trimmed]);
      addDirMutation.mutate(trimmed);
      setNewDir("");
    }
  };

  const handleRemoveDir = (dir: string) => {
    setDirectories(directories.filter((d) => d !== dir));
    removeDirMutation.mutate(dir);
  };

  const handleAddTool = () => {
    const trimmed = newTool.trim();
    if (trimmed && !allowedTools.includes(trimmed)) {
      setAllowedTools([...allowedTools, trimmed]);
      allowToolMutation.mutate(trimmed);
      setNewTool("");
    }
  };

  const handleRemoveTool = (tool: string) => {
    setAllowedTools(allowedTools.filter((t) => t !== tool));
    denyToolMutation.mutate(tool);
  };

  const permissionModes = [
    { value: "acceptEdits", label: "Accept Edits" },
    { value: "auto", label: "Auto" },
    { value: "dontAsk", label: "Don't Ask" },
    { value: "plan", label: "Plan" },
    { value: "bypassPermissions", label: "Bypass Permissions" },
  ];

  return (
    <div className="p-4 md:p-6 space-y-8">
      <CRTHeader text="Configuración" level={2} />

      {/* Theme Selection */}
      <section>
        <div className="flex items-center gap-3 mb-4">
          <Palette size={20} className="text-[var(--bw-accent)]" />
          <CRTHeader text="Tema Visual" level={3} />
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {themes.map((theme) => (
            <ThemeCard
              key={theme.id}
              theme={theme}
              isActive={theme.id === themeId}
              onSelect={() => setTheme(theme.id)}
            />
          ))}
        </div>
      </section>

      {/* Claude Permissions */}
      <section>
        <div className="flex items-center gap-3 mb-4">
          <Shield size={20} className="text-[var(--bw-accent)]" />
          <CRTHeader text="Permisos de Claude" level={3} />
        </div>

        {isLoading && (
          <div className="text-[var(--bw-muted)] animate-pulse">
            CARGANDO CONFIGURACIÓN...
          </div>
        )}

        <div className="grid gap-4 md:p-6 lg:grid-cols-2">
          {/* Permission Mode */}
          <CorpoCard title="MODO DE PERMISOS">
            <select
              value={permissionMode}
              onChange={(e) => setPermissionMode(e.target.value)}
              className="
                w-full bg-[var(--bw-surface)] border border-[var(--bw-border)]
                text-[var(--bw-fg)] text-sm px-3 py-2
                focus:border-[var(--bw-accent)] focus:outline-none
                font-mono
              "
            >
              {permissionModes.map((mode) => (
                <option key={mode.value} value={mode.value}>
                  {mode.label}
                </option>
              ))}
            </select>
          </CorpoCard>

          {/* Directories */}
          <CorpoCard title="DIRECTORIOS PERMITIDOS">
            <div className="space-y-2">
              {directories.length === 0 && (
                <p className="text-sm text-[var(--bw-muted)]">Sin directorios</p>
              )}
              {directories.map((dir) => (
                <div
                  key={dir}
                  className="flex items-center justify-between gap-2 p-2 bg-[var(--bw-surface-2)] rounded-sm"
                >
                  <span className="text-sm font-mono text-[var(--bw-fg)] truncate">
                    {dir}
                  </span>
                  <button
                    onClick={() => handleRemoveDir(dir)}
                    className="
                      p-1 text-[var(--bw-muted)] hover:text-[var(--bw-danger)]
                      transition-colors
                    "
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}

              <div className="flex gap-2 mt-3">
                <input
                  type="text"
                  value={newDir}
                  onChange={(e) => setNewDir(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleAddDir()}
                  placeholder="/path/to/dir"
                  className="
                    flex-1 bg-[var(--bw-bg)] border border-[var(--bw-border)]
                    text-[var(--bw-fg)] text-sm px-3 py-1.5
                    placeholder-[var(--bw-muted)]
                    focus:border-[var(--bw-accent)] focus:outline-none
                    font-mono
                  "
                />
                <GlyphButton
                  variant="secondary"
                  size="sm"
                  onClick={handleAddDir}
                  icon={<Plus size={14} />}
                >
                  Add
                </GlyphButton>
              </div>
            </div>
          </CorpoCard>

          {/* Allowed Tools */}
          <CorpoCard title="HERRAMIENTAS PERMITIDAS" className="lg:col-span-2">
            <div className="space-y-2">
              {allowedTools.length === 0 && (
                <p className="text-sm text-[var(--bw-muted)]">Sin herramientas</p>
              )}
              <div className="flex flex-wrap gap-2">
                {allowedTools.map((tool) => (
                  <div
                    key={tool}
                    className="
                      inline-flex items-center gap-2 px-3 py-1
                      bg-[var(--bw-accent)]/10 border border-[var(--bw-accent)]
                      text-sm font-mono text-[var(--bw-accent)]
                    "
                  >
                    {tool}
                    <button
                      onClick={() => handleRemoveTool(tool)}
                      className="hover:text-[var(--bw-danger)] transition-colors"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>

              <div className="flex gap-2 mt-3">
                <input
                  type="text"
                  value={newTool}
                  onChange={(e) => setNewTool(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleAddTool()}
                  placeholder="tool_name"
                  className="
                    flex-1 bg-[var(--bw-bg)] border border-[var(--bw-border)]
                    text-[var(--bw-fg)] text-sm px-3 py-1.5
                    placeholder-[var(--bw-muted)]
                    focus:border-[var(--bw-accent)] focus:outline-none
                    font-mono
                  "
                />
                <GlyphButton
                  variant="secondary"
                  size="sm"
                  onClick={handleAddTool}
                  icon={<Plus size={14} />}
                >
                  Add
                </GlyphButton>
              </div>
            </div>
          </CorpoCard>
        </div>

        {/* Save button */}
        <div className="flex items-center gap-4 mt-6">
          <GlyphButton
            onClick={() => saveMutation.mutate()}
            disabled={saveStatus === "saving"}
            icon={saveStatus === "saved" ? <Check size={14} /> : <Save size={14} />}
          >
            {saveStatus === "saving" ? "Guardando..." : "Guardar Configuración"}
          </GlyphButton>

          {saveStatus === "saved" && (
            <StatusDot status="online" size="sm" label="GUARDADO" />
          )}
          {saveStatus === "error" && (
            <StatusDot status="error" size="sm" label="ERROR" />
          )}
        </div>
      </section>

      {/* Model & Effort Defaults */}
      <section>
        <div className="flex items-center gap-3 mb-4">
          <Bot size={20} className="text-[var(--bw-accent)]" />
          <CRTHeader text="Model & Effort Defaults" level={3} />
        </div>

        <div className="grid gap-4 md:p-6 lg:grid-cols-2">
          <CorpoCard title="MODELO POR DEFECTO">
            <select
              value={defaultModel}
              onChange={(e) => setDefaultModel(e.target.value)}
              className="
                w-full bg-[var(--bw-surface)] border border-[var(--bw-border)]
                text-[var(--bw-fg)] text-sm px-3 py-2
                focus:border-[var(--bw-accent)] focus:outline-none
                font-mono
              "
            >
              <option value="haiku">Haiku (fast, cheap)</option>
              <option value="sonnet">Sonnet (balanced)</option>
              <option value="opus">Opus (powerful, expensive)</option>
            </select>
            <p className="text-xs text-[var(--bw-muted)] mt-2">
              Modelo usado cuando no hay agente activo.
            </p>
          </CorpoCard>

          <CorpoCard title="EFFORT POR DEFECTO">
            <select
              value={defaultEffort}
              onChange={(e) => setDefaultEffort(e.target.value)}
              className="
                w-full bg-[var(--bw-surface)] border border-[var(--bw-border)]
                text-[var(--bw-fg)] text-sm px-3 py-2
                focus:border-[var(--bw-accent)] focus:outline-none
                font-mono
              "
            >
              <option value="low">Low (quick answers)</option>
              <option value="medium">Medium (balanced)</option>
              <option value="high">High (thorough)</option>
              <option value="max">Max (most reasoning)</option>
            </select>
            <p className="text-xs text-[var(--bw-muted)] mt-2">
              Nivel de reasoning usado sin agente activo.
            </p>
          </CorpoCard>
        </div>

        <div className="flex items-center gap-4 mt-6">
          <GlyphButton
            onClick={() => saveMutation.mutate()}
            disabled={saveStatus === "saving"}
            icon={saveStatus === "saved" ? <Check size={14} /> : <Save size={14} />}
          >
            {saveStatus === "saving" ? "Guardando..." : "Guardar Configuración"}
          </GlyphButton>
        </div>
      </section>

      {/* Secrets Management */}
      <SecretsSection />

      {/* Claude Authentication */}
      <ClaudeAuthSection />
    </div>
  );
}

// ============================================================================
// Secrets Section Component
// ============================================================================

interface SecretInfo {
  name: string;
  createdAt: number;
  updatedAt: number;
}

interface SecretsResponse {
  secrets: SecretInfo[];
}

function SecretsSection() {
  const queryClient = useQueryClient();
  const [newSecretName, setNewSecretName] = useState("");
  const [newSecretValue, setNewSecretValue] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: secretsData, isLoading } = useQuery<SecretsResponse>({
    queryKey: ["secrets"],
    queryFn: async () => {
      const res = await fetch("/api/secrets");
      return res.json();
    },
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/secrets", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: newSecretName.toUpperCase().replace(/[^A-Z0-9_]/g, "_"),
          value: newSecretValue,
        }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error ?? "Failed to create secret");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["secrets"] });
      setNewSecretName("");
      setNewSecretValue("");
      setShowModal(false);
      setError(null);
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : "Unknown error");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (name: string) => {
      const res = await fetch(`/api/secrets/${encodeURIComponent(name)}`, {
        method: "DELETE",
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error ?? "Failed to delete secret");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["secrets"] });
    },
  });

  const handleDelete = (name: string) => {
    if (confirm(`Delete secret "${name}"? MCP servers using this secret will fail.`)) {
      deleteMutation.mutate(name);
    }
  };

  const secrets = secretsData?.secrets ?? [];

  return (
    <section>
      <div className="flex items-center gap-3 mb-4">
        <Key size={20} className="text-[var(--bw-accent)]" />
        <CRTHeader text="Secrets" level={3} />
      </div>

      <p className="text-sm text-[var(--bw-muted)] mb-4">
        Encrypted secrets for MCP servers. Use <code className="text-[var(--bw-accent)]">${'{'}secret:NAME{'}'}</code> in MCP server environment variables.
      </p>

      <CorpoCard title="STORED SECRETS">
        {isLoading && (
          <div className="flex items-center gap-2 text-[var(--bw-muted)]">
            <Loader2 size={14} className="animate-spin" />
            Loading...
          </div>
        )}

        {!isLoading && secrets.length === 0 && (
          <p className="text-sm text-[var(--bw-muted)]">No secrets stored</p>
        )}

        <div className="space-y-2">
          {secrets.map((secret) => (
            <div
              key={secret.name}
              className="flex items-center justify-between gap-2 p-2 bg-[var(--bw-surface-2)] rounded-sm"
            >
              <div className="flex-1">
                <span className="font-mono text-sm text-[var(--bw-accent)]">
                  {secret.name}
                </span>
                <span className="ml-2 text-xs text-[var(--bw-muted)]">
                  •••••••• (updated {new Date(secret.updatedAt).toLocaleDateString()})
                </span>
              </div>
              <button
                onClick={() => handleDelete(secret.name)}
                disabled={deleteMutation.isPending}
                className="p-1 text-[var(--bw-muted)] hover:text-[var(--bw-danger)] transition-colors"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>

        <div className="mt-4">
          <GlyphButton
            variant="secondary"
            size="sm"
            onClick={() => setShowModal(true)}
            icon={<Plus size={14} />}
          >
            ADD SECRET
          </GlyphButton>
        </div>
      </CorpoCard>

      {/* Add Secret Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
          <div className="w-full max-w-md mx-4 bg-[var(--bw-bg)] border border-[var(--bw-border)] p-4 md:p-6">
            <div className="flex items-center justify-between mb-4">
              <CRTHeader text="Add Secret" level={3} />
              <button
                onClick={() => {
                  setShowModal(false);
                  setError(null);
                }}
                className="text-[var(--bw-muted)] hover:text-[var(--bw-fg)]"
              >
                <X size={20} />
              </button>
            </div>

            {error && (
              <div className="mb-4 p-2 bg-[var(--bw-danger)]/10 border border-[var(--bw-danger)] text-[var(--bw-danger)] text-sm">
                {error}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-xs text-[var(--bw-muted)] mb-1">
                  Name (UPPER_SNAKE_CASE)
                </label>
                <input
                  type="text"
                  value={newSecretName}
                  onChange={(e) =>
                    setNewSecretName(e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, "_"))
                  }
                  placeholder="MI_SECRET"
                  className="w-full bg-[var(--bw-surface)] border border-[var(--bw-border)] px-3 py-2 text-sm text-[var(--bw-fg)] font-mono focus:border-[var(--bw-accent)] focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs text-[var(--bw-muted)] mb-1">
                  Value (will be encrypted)
                </label>
                <input
                  type="password"
                  value={newSecretValue}
                  onChange={(e) => setNewSecretValue(e.target.value)}
                  placeholder="•••••••••••••••"
                  className="w-full bg-[var(--bw-surface)] border border-[var(--bw-border)] px-3 py-2 text-sm text-[var(--bw-fg)] font-mono focus:border-[var(--bw-accent)] focus:outline-none"
                />
              </div>
            </div>

            <div className="flex gap-2 mt-6">
              <GlyphButton
                variant="primary"
                onClick={() => createMutation.mutate()}
                disabled={createMutation.isPending || !newSecretName || !newSecretValue}
                className="flex-1"
              >
                {createMutation.isPending ? (
                  <Loader2 className="animate-spin" size={14} />
                ) : (
                  "SAVE SECRET"
                )}
              </GlyphButton>
              <GlyphButton
                variant="ghost"
                onClick={() => {
                  setShowModal(false);
                  setError(null);
                }}
              >
                CANCEL
              </GlyphButton>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

// ============================================================================
// Claude Authentication Section
// ============================================================================

function getStatusIndicator(status: AuthStatus | undefined) {
  if (!status) return { color: "gray", label: "UNKNOWN", icon: "⚪" };
  if (!status.loggedIn || status.isExpired) {
    return { color: "red", label: "EXPIRED", icon: "🔴" };
  }
  if (status.isExpiringSoon) {
    return { color: "yellow", label: "EXPIRING SOON", icon: "🟡" };
  }
  return { color: "green", label: "ACTIVE", icon: "🟢" };
}

function formatTimeLeft(ms: number | null): string {
  if (ms === null || ms <= 0) return "EXPIRED";
  const hours = Math.floor(ms / (1000 * 60 * 60));
  const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
  if (hours > 24) {
    const days = Math.floor(hours / 24);
    return `${days}d ${hours % 24}h`;
  }
  return `${hours}h ${minutes}m`;
}

function formatLogTime(timestamp: number): string {
  return new Date(timestamp).toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

function ClaudeAuthSection() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { data: status, isLoading: statusLoading, refetch: refetchStatus } = useClaudeAuthStatus();
  const { data: logEntries, isLoading: logLoading } = useClaudeAuthLog(20);
  const login = useClaudeLogin();
  const logoutMutation = useClaudeLogout();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [pendingCode, setPendingCode] = useState("");

  // Auto-close modal on successful login
  useEffect(() => {
    if (login.isCompleted && showLoginModal) {
      const timer = setTimeout(() => {
        setShowLoginModal(false);
        login.reset();
        queryClient.invalidateQueries({ queryKey: ["claude-auth-status"] });
        queryClient.invalidateQueries({ queryKey: ["claude-auth-log"] });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [login.isCompleted, showLoginModal, login, queryClient]);

  const statusIndicator = getStatusIndicator(status);

  const handleCheckStatus = () => {
    refetchStatus();
  };

  const handleReauthenticate = (mode: "session" | "setup-token" = "session") => {
    login.reset();
    setPendingCode("");
    setShowLoginModal(true);
    login.startLogin(mode);
  };

  const handleLogout = () => {
    if (confirm("¿Cerrar sesión de Claude? Esto invalidará la autenticación actual.")) {
      logoutMutation.mutate();
    }
  };

  const handleCopyUrl = (url: string) => {
    navigator.clipboard.writeText(url);
  };

  const handleCloseModal = () => {
    login.cancel();
    login.reset();
    setPendingCode("");
    setShowLoginModal(false);
    queryClient.invalidateQueries({ queryKey: ["claude-auth-status"] });
    queryClient.invalidateQueries({ queryKey: ["claude-auth-log"] });
  };

  return (
    <section>
      <div className="flex items-center gap-3 mb-4">
        <KeyRound size={20} className="text-[var(--bw-accent)]" />
        <CRTHeader text="Claude Authentication" level={3} />
      </div>

      <div className="grid gap-4 md:p-6 lg:grid-cols-2">
        {/* Auth Status Card */}
        <CorpoCard title="🔑 AUTHENTICATION STATUS">
          {statusLoading ? (
            <div className="flex items-center gap-2 text-[var(--bw-muted)]">
              <Loader2 size={14} className="animate-spin" />
              Loading...
            </div>
          ) : (
            <div className="space-y-3">
              {/* Status row */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-[var(--bw-muted)]">Status:</span>
                <span
                  className={`text-sm font-bold ${
                    statusIndicator.color === "green"
                      ? "text-green-400"
                      : statusIndicator.color === "yellow"
                      ? "text-yellow-400"
                      : "text-red-400"
                  }`}
                >
                  {statusIndicator.icon} {statusIndicator.label}
                </span>
              </div>

              {/* Method */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-[var(--bw-muted)]">Method:</span>
                <span className="text-sm text-[var(--bw-fg)] font-mono">
                  {status?.authMethod ?? "N/A"}
                </span>
              </div>

              {/* Email */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-[var(--bw-muted)]">Email:</span>
                <span className="text-sm text-[var(--bw-accent)] font-mono">
                  {status?.email ?? "N/A"}
                </span>
              </div>

              {/* Subscription */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-[var(--bw-muted)]">Subscription:</span>
                <span className="text-sm text-[var(--bw-fg)] font-bold uppercase">
                  {status?.subscriptionType ?? "N/A"}
                </span>
              </div>

              {/* Expires at */}
              {status?.expiresAt && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[var(--bw-muted)]">Expires at:</span>
                  <span className="text-sm text-[var(--bw-fg)] font-mono">
                    {new Date(status.expiresAt).toLocaleString("en-US", {
                      dateStyle: "short",
                      timeStyle: "short",
                    })}
                  </span>
                </div>
              )}

              {/* Time left */}
              {status && status.expiresInMs !== null && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[var(--bw-muted)]">Time left:</span>
                  <span
                    className={`text-sm font-mono ${
                      status.isExpired
                        ? "text-red-400"
                        : status.isExpiringSoon
                        ? "text-yellow-400"
                        : "text-green-400"
                    }`}
                  >
                    <Clock size={12} className="inline mr-1" />
                    {formatTimeLeft(status.expiresInMs)}
                  </span>
                </div>
              )}

              {/* Warning if expiring soon */}
              {status?.isExpiringSoon && !status?.isExpired && (
                <div className="flex items-center gap-2 p-2 mt-2 bg-yellow-500/10 border border-yellow-500 text-yellow-400 text-xs">
                  <AlertTriangle size={14} />
                  Token expiring soon! Reauthenticate to avoid interruption.
                </div>
              )}

              {/* Error if expired */}
              {status?.isExpired && (
                <div className="flex items-center gap-2 p-2 mt-2 bg-red-500/10 border border-red-500 text-red-400 text-xs">
                  <AlertTriangle size={14} />
                  Token expired! Reauthenticate immediately.
                </div>
              )}
            </div>
          )}

          {/* Action buttons */}
          <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-[var(--bw-border)]">
            <GlyphButton
              variant="secondary"
              size="sm"
              onClick={handleCheckStatus}
              disabled={statusLoading}
              icon={<RefreshCw size={14} className={statusLoading ? "animate-spin" : ""} />}
            >
              CHECK
            </GlyphButton>
            <GlyphButton
              variant="primary"
              size="sm"
              onClick={() => navigate({ to: "/ops" })}
              icon={<Terminal size={14} />}
              title="Open the /ops terminal and run `claude /login` to refresh credentials. Most reliable path."
            >
              RE-LOGIN VIA TERMINAL
            </GlyphButton>
            <GlyphButton
              variant="ghost"
              size="sm"
              onClick={() => handleReauthenticate("session")}
              icon={<Key size={14} />}
              title="Experimental: refresh session via embedded modal (may hang in some edge cases)."
            >
              MODAL (EXPERIMENTAL)
            </GlyphButton>
            <GlyphButton
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              disabled={logoutMutation.isPending || !status?.loggedIn}
              icon={<LogOut size={14} />}
            >
              LOGOUT
            </GlyphButton>
          </div>
        </CorpoCard>

        {/* Auth Log Card */}
        <CorpoCard title="📜 AUTH LOG">
          {logLoading ? (
            <div className="flex items-center gap-2 text-[var(--bw-muted)]">
              <Loader2 size={14} className="animate-spin" />
              Loading...
            </div>
          ) : !logEntries || logEntries.length === 0 ? (
            <p className="text-sm text-[var(--bw-muted)]">No log entries</p>
          ) : (
            <div className="space-y-1 max-h-64 overflow-y-auto font-mono text-xs">
              {logEntries.map((entry: ClaudeAuthLogEntry) => (
                <div
                  key={entry.id}
                  className="flex items-center gap-2 py-1 border-b border-[var(--bw-border)]/30 last:border-0"
                >
                  <span className="text-[var(--bw-muted)] w-12 flex-shrink-0">
                    {formatLogTime(entry.createdAt)}
                  </span>
                  <span
                    className={`flex-1 ${
                      entry.event.includes("completed")
                        ? "text-green-400"
                        : entry.event.includes("failed") || entry.event.includes("expiry")
                        ? "text-red-400"
                        : "text-[var(--bw-fg)]"
                    }`}
                  >
                    {entry.event}
                  </span>
                  {entry.subscriptionType && (
                    <span className="text-[var(--bw-accent)]">
                      {entry.subscriptionType}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </CorpoCard>
      </div>

      {/* Login Modal */}
      {showLoginModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80">
          <div className="w-full max-w-lg mx-4 bg-[var(--bw-bg)] border border-[var(--bw-border)] p-6">
            <div className="flex items-center justify-between mb-4">
              <CRTHeader
                text={
                  login.mode === "setup-token"
                    ? "Generate Setup Token"
                    : "Refresh Claude Session"
                }
                level={3}
              />
              <button
                onClick={handleCloseModal}
                className="text-[var(--bw-muted)] hover:text-[var(--bw-fg)]"
              >
                <X size={20} />
              </button>
            </div>

            <div className="mb-4 p-2 text-xs border border-[var(--bw-border)] text-[var(--bw-muted)]">
              {login.mode === "setup-token" ? (
                <>
                  Generates a long-lived <code className="text-[var(--bw-accent)]">sk-ant-…</code>{" "}
                  API key you can paste into OpenClaw or any other tool.
                </>
              ) : (
                <>
                  Refreshes Black Wall's own{" "}
                  <code className="text-[var(--bw-accent)]">
                    ~/.claude/.credentials.json
                  </code>{" "}
                  so the backend keeps calling the Claude CLI.
                </>
              )}
            </div>

            {/* Status indicator */}
            <div className="space-y-4">
              {/* Events log */}
              <div className="bg-[var(--bw-surface)] border border-[var(--bw-border)] p-3 max-h-40 overflow-y-auto font-mono text-xs">
                {login.events.length === 0 ? (
                  <div className="flex items-center gap-2 text-[var(--bw-muted)]">
                    <Loader2 size={12} className="animate-spin" />
                    Initiating login flow...
                  </div>
                ) : (
                  login.events.map((event, i) => (
                    <div
                      key={i}
                      className={`py-0.5 ${
                        event.type === "error"
                          ? "text-red-400"
                          : event.type === "completed"
                          ? "text-green-400"
                          : "text-[var(--bw-muted)]"
                      }`}
                    >
                      {event.type === "stdout" || event.type === "stderr"
                        ? event.line
                        : event.type === "error"
                        ? `❌ ${event.message}`
                        : event.type === "completed"
                        ? `✅ Logged in as ${event.email} (${event.subscriptionType})`
                        : event.type}
                    </div>
                  ))
                )}
              </div>

              {/* Device code URL + paste code */}
              {login.deviceCodeUrl && !login.isCompleted && !login.error && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <p className="text-sm font-bold text-[var(--bw-fg)]">
                      1. Open this URL in any browser and sign in:
                    </p>
                    <div className="p-3 bg-[var(--bw-accent)]/10 border border-[var(--bw-accent)] break-all">
                      <code className="text-sm text-[var(--bw-accent)]">
                        {login.deviceCodeUrl}
                      </code>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <GlyphButton
                        variant="primary"
                        size="sm"
                        onClick={() => handleCopyUrl(login.deviceCodeUrl!)}
                        icon={<Copy size={14} />}
                      >
                        COPY URL
                      </GlyphButton>
                      <GlyphButton
                        variant="secondary"
                        size="sm"
                        onClick={() =>
                          window.open(login.deviceCodeUrl!, "_blank")
                        }
                        icon={<ExternalLink size={14} />}
                      >
                        OPEN IN BROWSER
                      </GlyphButton>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <p className="text-sm font-bold text-[var(--bw-fg)]">
                      2. Paste the authorization code you get back:
                    </p>
                    <p className="text-xs text-[var(--bw-muted)]">
                      After you sign in, Claude redirects to a page showing a
                      code like{" "}
                      <code className="text-[var(--bw-accent)]">
                        abc...#xyz
                      </code>
                      . Copy it and paste below.
                    </p>
                    <textarea
                      value={pendingCode}
                      onChange={(e) => setPendingCode(e.target.value)}
                      placeholder="Paste the code here…"
                      rows={2}
                      className="w-full bg-[var(--bw-bg)] border border-[var(--bw-border)] text-[var(--bw-fg)] text-xs font-mono p-2 focus:border-[var(--bw-accent)] focus:outline-none resize-none break-all"
                      spellCheck={false}
                      autoCapitalize="off"
                      autoCorrect="off"
                    />
                    <GlyphButton
                      variant="primary"
                      size="sm"
                      onClick={async () => {
                        await login.submitCode(pendingCode);
                        setPendingCode("");
                      }}
                      disabled={
                        !pendingCode.trim() || login.phase === "submitting"
                      }
                      className="w-full"
                    >
                      {login.phase === "submitting"
                        ? "VERIFYING…"
                        : "SUBMIT CODE"}
                    </GlyphButton>
                  </div>
                </div>
              )}

              {/* Completed state */}
              {login.isCompleted && (
                <div className="space-y-4 py-2">
                  <div className="text-center">
                    <div className="text-4xl mb-2">✅</div>
                    <p className="text-green-400 font-bold">
                      Setup Token Generated!
                    </p>
                  </div>

                  {login.setupToken ? (
                    <div className="space-y-2">
                      <p className="text-xs text-[var(--bw-muted)]">
                        Copy this token and store it somewhere safe — you
                        won't see it again:
                      </p>
                      <div className="p-3 bg-[var(--bw-accent)]/10 border border-[var(--bw-accent)] break-all">
                        <code className="text-xs text-[var(--bw-accent)]">
                          {login.setupToken}
                        </code>
                      </div>
                      <GlyphButton
                        variant="primary"
                        size="sm"
                        onClick={() => handleCopyUrl(login.setupToken!)}
                        icon={<Copy size={14} />}
                        className="w-full"
                      >
                        COPY TOKEN
                      </GlyphButton>
                    </div>
                  ) : (
                    <p className="text-sm text-[var(--bw-muted)] text-center">
                      Credentials refreshed. You can close this modal.
                    </p>
                  )}
                </div>
              )}

              {/* Error state */}
              {login.error && !login.isCompleted && (
                <div className="space-y-3">
                  <div className="p-3 bg-red-500/10 border border-red-500 text-red-400">
                    {login.error}
                  </div>
                  <GlyphButton
                    variant="primary"
                    onClick={() => handleReauthenticate(login.mode)}
                    className="w-full"
                  >
                    RETRY
                  </GlyphButton>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex justify-end mt-6 pt-4 border-t border-[var(--bw-border)]">
              <GlyphButton variant="ghost" onClick={handleCloseModal}>
                {login.isCompleted ? "CLOSE" : "CANCEL"}
              </GlyphButton>
            </div>
          </div>
        </div>
      )}

    </section>
  );
}
